<script lang="ts">
/**
 * Truncated item list for a container's contents.
 *
 * Shows items nested inside a container with a "Show all N items"
 * link when the list is truncated. Used on container detail pages.
 *
 * @example
 * ```vue
 * <ItemContainerChildren
 *   :items="container.items.slice(0, 10)"
 *   :total-count="container.item_count"
 *   :see-all-to="itemsPath({ container_id: container.id })"
 * />
 * ```
 */
export interface ContainerChildrenProps {
  /** Items to display (may be a truncated subset). */
  items: ItemSummary[]
  /** Total number of items in the container. */
  totalCount: number
  /** Link to the full items list. Shown when items are truncated. */
  seeAllTo?: string
}
</script>

<script setup lang="ts">
import { SbList, SbListLabel } from "@unio/saltbox-ui"

import ListItemRow from "@/components/ListItemRow.vue"
import { ICON } from "@/lib/icons"
import type { ItemSummary } from "@/types/models"

defineProps<ContainerChildrenProps>()
</script>

<template>
  <SbList separator>
    <SbListLabel header>Contents ({{ totalCount }} items)</SbListLabel>
    <ListItemRow v-for="item in items" :key="item.id" :item="item" variant="nested" />
    <div v-if="seeAllTo && items.length < totalCount" class="py-2">
      <UButton
        :label="`Show all ${totalCount} items`"
        variant="link"
        color="primary"
        :trailing-icon="ICON.arrowRight"
        size="sm"
        :to="seeAllTo"
      />
    </div>
  </SbList>
</template>
